import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import axios from 'axios';
import {
  Consultation,
  ConsultationMistake,
  ConsultationTask,
  ClientInfoName,
  MistakeCategory,
  generateAiInsightsAsync,
  fetchAiInsightsJobAsync,
  clearAiInsightsAsync,
  upsertClientInfoAsync,
  deleteClientInfoAsync,
  updateTaskAsync,
  deleteTaskAsync,
  updateMistakeAsync,
  deleteMistakeAsync
} from '../store/slices/consultationsSlice';
import { updateRecordingAsync } from '../store/slices/recordingsSlice';
import { getApiErrorMessage } from '../utils/apiError';
import AiBadge from './AiBadge';
import ConfirmSpeakersModal from './ConfirmSpeakersModal';
import ConfirmationModal from './ConfirmationModal';
import Select, { SelectOption } from './Select';
import './ConsultationAIInsightsSection.css';

interface Props {
  consultation: Consultation;
  onRefresh: () => void | Promise<void>;
}

const POLL_INTERVAL_MS = 15_000;
const API_BASE_URL = import.meta.env.VITE_API_URL as string;

const FIELD_DEFS: Array<{ name: ClientInfoName; label: string; multiline: boolean; placeholder: string }> = [
  { name: 'goal', label: 'Goal', multiline: false, placeholder: "Student's learning goal" },
  { name: 'profession', label: 'Profession', multiline: false, placeholder: "Student's profession" },
  { name: 'general_insights', label: 'General insights', multiline: true, placeholder: 'Interest level, prior study, anything notable' }
];

const MISTAKE_CATEGORIES: { value: MistakeCategory; label: string }[] = [
  { value: 'grammar', label: 'Grammar' },
  { value: 'calque', label: 'Calque' },
  { value: 'vocabulary', label: 'Vocabulary' },
  { value: 'code_switching', label: 'Code-switching' },
  { value: 'pronunciation', label: 'Pronunciation' },
  { value: 'other', label: 'Other' }
];

const MISTAKE_CATEGORY_OPTIONS: SelectOption<MistakeCategory>[] = MISTAKE_CATEGORIES.map((c) => ({
  value: c.value,
  label: c.label
}));

function formatMs(ms: number | null): string {
  if (ms == null) return '';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const ConsultationAIInsightsSection: React.FC<Props> = ({ consultation, onRefresh }) => {
  const dispatch = useAppDispatch();
  const { token } = useAppSelector((state) => state.auth);

  const job = consultation.aiInsightsJob || null;
  const recording = consultation.recording || null;
  const clientInfo = consultation.clientInfo || [];
  const tasks = consultation.tasks || [];
  const mistakes = consultation.mistakes || [];

  const transcriptReady = !!(recording && recording.activeJobId);
  const isRunning = !!(job && (job.status === 'pending' || job.status === 'processing'));
  const hasPriorData = !!(job || clientInfo.some(c => c.isAi) || tasks.some(t => t.isAi) || mistakes.some(m => m.isAi));
  const speakerLocked = !!job;
  const hasViewableInsights = !!(
    (job && job.status === 'completed')
    || clientInfo.length > 0
    || tasks.length > 0
    || mistakes.length > 0
  );

  const [speakersOpen, setSpeakersOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [swapError, setSwapError] = useState<string | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const [clearModalOpen, setClearModalOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isRunning) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(async () => {
      const latest = await dispatch(fetchAiInsightsJobAsync(consultation.id)).unwrap().catch(() => null);
      if (latest && latest.status !== 'pending' && latest.status !== 'processing') {
        await onRefresh();
      }
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isRunning, consultation.id, dispatch, onRefresh]);

  const staleTranscript = useMemo(() => {
    if (!job || job.status !== 'completed') return false;
    const ajCompleted = recording?.activeJob?.completedAt;
    if (!ajCompleted || !job.completedAt) return false;
    return new Date(ajCompleted).getTime() > new Date(job.completedAt).getTime();
  }, [job, recording]);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [playingMistakeId, setPlayingMistakeId] = useState<number | null>(null);
  const pauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ensureAudioUrl = useCallback(async (): Promise<string | null> => {
    if (audioUrl) return audioUrl;
    if (!recording) return null;
    try {
      const res = await axios.post(
        `${API_BASE_URL}/recordings/${recording.id}/audio-url`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setAudioUrl(res.data.url);
      return res.data.url;
    } catch {
      return null;
    }
  }, [audioUrl, recording, token]);

  const stopSegment = useCallback(() => {
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
    const el = audioRef.current;
    if (el && el.src) {
      try {
        el.pause();
      } catch {
        // jsdom has no media implementation
      }
    }
    setPlayingMistakeId(null);
  }, []);

  const playSegment = useCallback(async (mistakeId: number, startMs: number, endMs: number | null) => {
    const url = await ensureAudioUrl();
    if (!url || !audioRef.current) return;
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
    if (audioRef.current.src !== url) audioRef.current.src = url;
    audioRef.current.currentTime = startMs / 1000;
    audioRef.current.play().catch(() => { /* ignore */ });
    setPlayingMistakeId(mistakeId);
    if (endMs != null && endMs > startMs) {
      pauseTimerRef.current = setTimeout(() => {
        if (audioRef.current) audioRef.current.pause();
        setPlayingMistakeId(null);
      }, endMs - startMs + 200);
    }
  }, [ensureAudioUrl]);

  useEffect(() => () => {
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    stopSegment();
  }, [stopSegment]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const handleOpenSpeakers = () => {
    setGenError(null);
    setSwapError(null);
    close();
    setSpeakersOpen(true);
  };

  const handleSwap = useCallback(async () => {
    if (!recording) return;
    setSwapError(null);
    const next = recording.channelTeacher === 1 ? 2 : 1;
    try {
      await dispatch(updateRecordingAsync({ id: recording.id, patch: { channelTeacher: next } })).unwrap();
      await onRefresh();
    } catch (err) {
      const msg = typeof err === 'string' ? err : getApiErrorMessage(err, 'Failed to swap speakers');
      setSwapError(msg);
    }
  }, [recording, dispatch, onRefresh]);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setGenError(null);
    try {
      await dispatch(generateAiInsightsAsync(consultation.id)).unwrap();
      setSpeakersOpen(false);
      await onRefresh();
    } catch (err) {
      setGenError(typeof err === 'string' ? err : getApiErrorMessage(err, 'Failed to start insights job'));
    } finally {
      setGenerating(false);
    }
  }, [consultation.id, dispatch, onRefresh]);

  const handleClear = useCallback(async () => {
    try {
      await dispatch(clearAiInsightsAsync(consultation.id)).unwrap();
      setClearModalOpen(false);
      await onRefresh();
    } catch {
      // surface inline if needed; cleared modal stays open on error.
    }
  }, [consultation.id, dispatch, onRefresh]);

  const reRunBlocked = job?.status === 'completed' && !staleTranscript;
  const generateDisabled = !transcriptReady || isRunning || reRunBlocked;
  const generateTitle = !transcriptReady
    ? 'Transcribe a recording first.'
    : reRunBlocked
      ? 'Re-run is allowed only after the recording is re-transcribed. Clear insights to start over.'
      : job && job.status === 'failed' && job.lastError
        ? `Last run failed: ${job.lastError}`
        : '';

  const generateLabel = isRunning ? 'Processing…' : hasPriorData ? 'Re-run' : 'Generate insights';

  return (
    <>
      <section className="consultation-edit__card insights-card">
        <div className="insights-card__head">
          <div className="insights-card__heading">
            <h2 className="consultation-edit__card-title">Insights</h2>
            {job && job.status === 'completed' && job.completedAt && (
              <div className="insights-card__meta">
                Last run {new Date(job.completedAt).toLocaleString()}
              </div>
            )}
          </div>
          <div className="insights-card__actions">
            <button
              type="button"
              className={hasPriorData ? 'insights-card__rerun' : 'btn-primary'}
              onClick={handleOpenSpeakers}
              disabled={generateDisabled}
              title={generateTitle}
            >
              {generateLabel}
            </button>
            {mistakes.length > 0 && (
              <button
                type="button"
                className="btn-primary"
                onClick={() => setOpen(true)}
              >
                View mistakes ({mistakes.length})
              </button>
            )}
            {isRunning && hasViewableInsights && (
              <span className="insights-card__status">Processing…</span>
            )}
          </div>
        </div>

        {!transcriptReady && (
          <p className="insights-card__note">
            Upload a recording and wait for transcription to enable AI extraction.
          </p>
        )}

        {job && job.status === 'failed' && job.lastError && (
          <div className="insights-card__banner insights-card__banner--fail">
            <strong>Last run failed:</strong> {job.lastError}
          </div>
        )}

        {staleTranscript && (
          <div className="insights-card__banner insights-card__banner--warn">
            Transcript was re-run after these insights were generated — they may be out of date. Re-run to refresh.
          </div>
        )}

        {genError && (
          <div className="insights-card__error">{genError}</div>
        )}

        {isRunning && (
          <p className="insights-card__note">
            {hasViewableInsights ? 'A new run is processing…' : 'Processing…'}
          </p>
        )}

        <ConversionPotentialField clientInfo={clientInfo} />
        {FIELD_DEFS.map(def => (
          <ClientInfoField
            key={def.name}
            def={def}
            consultationId={consultation.id}
            existing={clientInfo.find(c => c.name === def.name) || null}
            onRefresh={onRefresh}
          />
        ))}

        <TasksList
          consultationId={consultation.id}
          tasks={tasks}
          onRefresh={onRefresh}
        />

        {hasPriorData && !isRunning && (
          <div className="insights-card__clear">
            <button
              type="button"
              className="btn-danger"
              onClick={() => setClearModalOpen(true)}
            >
              Clear AI-generated insights
            </button>
            <small className="insights-card__clear-hint">
              Removes AI-generated values, tasks, and mistakes (manual ones survive) and unlocks the speaker mapping.
            </small>
          </div>
        )}
      </section>

      {open && createPortal(
        <div className="insights-dialog-overlay">
          <div
            className="insights-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="insights-dialog-title"
          >
            <div className="insights-dialog__top">
              <div className="insights-dialog__heading">
                <h2 id="insights-dialog-title" className="insights-dialog__title">
                  Mistakes ({mistakes.length})
                </h2>
              </div>
              <div className="insights-dialog__actions">
                <button
                  type="button"
                  className="insights-dialog__close"
                  onClick={close}
                  aria-label="Close mistakes"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="insights-dialog__body">
              <MistakesList
                consultationId={consultation.id}
                mistakes={mistakes}
                onRefresh={onRefresh}
                onPlay={playSegment}
                onStop={stopSegment}
                playingMistakeId={playingMistakeId}
                canPlay={!!recording}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      <audio
        ref={audioRef}
        style={{ display: 'none' }}
        controls={false}
        onEnded={() => setPlayingMistakeId(null)}
        onPause={() => setPlayingMistakeId(null)}
      />

      <ConfirmSpeakersModal
        isOpen={speakersOpen}
        channelTeacher={recording?.channelTeacher || 1}
        sampleQuotes={recording?.sampleQuotes || null}
        locked={speakerLocked}
        hasPriorData={hasPriorData}
        onSwap={handleSwap}
        onGenerate={handleGenerate}
        onCancel={() => setSpeakersOpen(false)}
        submitting={generating}
        swapError={swapError}
        unhealthy={recording?.healthy === false}
        unhealthyReason={recording?.healthReason}
      />

      <ConfirmationModal
        isOpen={clearModalOpen}
        title="Clear AI-generated insights?"
        message="This removes all AI-generated values, tasks, and mistakes from this consultation. Manual entries are kept."
        confirmText="Clear"
        onConfirm={handleClear}
        onCancel={() => setClearModalOpen(false)}
        dangerous
      />
    </>
  );
};

const CONVERSION_LEVELS: Record<string, { label: string; className: string }> = {
  low: { label: 'Low', className: 'insights-conversion__pill--low' },
  moderate: { label: 'Moderate', className: 'insights-conversion__pill--moderate' },
  high: { label: 'High', className: 'insights-conversion__pill--high' }
};

const ConversionPotentialField: React.FC<{
  clientInfo: Array<{ name: ClientInfoName; value: string | null; isAi: boolean }>;
}> = ({ clientInfo }) => {
  const levelRow = clientInfo.find(c => c.name === 'conversion_potential') || null;
  const reasoning = clientInfo.find(c => c.name === 'conversion_reasoning')?.value || '';
  const level = levelRow?.value || '';
  const meta = CONVERSION_LEVELS[level];

  return (
    <div className="insights-conversion">
      <p className="insights-conversion__label">
        Conversion potential
        {levelRow?.isAi && <AiBadge />}
      </p>
      {meta ? (
        <span className={`insights-conversion__pill ${meta.className}`}>{meta.label}</span>
      ) : (
        <span className="insights-conversion__unset">Not assessed</span>
      )}
      {meta && reasoning && (
        <p className="insights-conversion__reason">{reasoning}</p>
      )}
    </div>
  );
};

const ClientInfoField: React.FC<{
  def: { name: ClientInfoName; label: string; multiline: boolean; placeholder: string };
  consultationId: number;
  existing: { name: ClientInfoName; value: string | null; isAi: boolean } | null;
  onRefresh: () => void | Promise<void>;
}> = ({ def, consultationId, existing, onRefresh }) => {
  const dispatch = useAppDispatch();
  const [value, setValue] = useState<string>(existing?.value || '');
  const [saving, setSaving] = useState(false);
  const isAi = !!existing?.isAi;

  const [prevExistingValue, setPrevExistingValue] = useState<string | null | undefined>(existing?.value);
  if (existing?.value !== prevExistingValue) {
    setPrevExistingValue(existing?.value);
    setValue(existing?.value || '');
  }

  const persist = useCallback(async () => {
    const next = value.trim();
    const current = existing?.value || '';
    if (next === current) return;
    setSaving(true);
    try {
      if (next === '') {
        if (existing) {
          await dispatch(deleteClientInfoAsync({ consultationId, name: def.name })).unwrap();
        }
      } else {
        await dispatch(upsertClientInfoAsync({ consultationId, name: def.name, value: next })).unwrap();
      }
      await onRefresh();
    } finally {
      setSaving(false);
    }
  }, [value, existing, dispatch, consultationId, def.name, onRefresh]);

  return (
    <div className="insights-field">
      <label>
        {def.label}
        {isAi && <AiBadge />}
        {saving && <small className="insights-field__saving">Saving…</small>}
      </label>
      {def.multiline ? (
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={persist}
          placeholder={def.placeholder}
          className="form-input"
          rows={5}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={persist}
          placeholder={def.placeholder}
          className="form-input"
        />
      )}
    </div>
  );
};

const TasksList: React.FC<{
  consultationId: number;
  tasks: ConsultationTask[];
  onRefresh: () => void | Promise<void>;
}> = ({ consultationId, tasks, onRefresh }) => {
  return (
    <div className="insights-card__section">
      <h3 className="insights-card__section-title">Tasks</h3>
      {tasks.length === 0 && (
        <p className="insights-dialog__empty">No tasks yet.</p>
      )}
      {tasks.map(task => (
        <TaskRow key={task.id} task={task} consultationId={consultationId} onRefresh={onRefresh} />
      ))}
    </div>
  );
};

const TaskRow: React.FC<{
  task: ConsultationTask;
  consultationId: number;
  onRefresh: () => void | Promise<void>;
}> = ({ task, consultationId, onRefresh }) => {
  const dispatch = useAppDispatch();
  const [description, setDescription] = useState(task.description);

  const [prevDescription, setPrevDescription] = useState(task.description);
  if (task.description !== prevDescription) {
    setPrevDescription(task.description);
    setDescription(task.description);
  }

  const persistDescription = async () => {
    const next = description.trim();
    if (!next || next === task.description) return;
    await dispatch(updateTaskAsync({ consultationId, taskId: task.id, patch: { description: next } })).unwrap().catch(() => null);
    await onRefresh();
  };

  const toggleDone = async () => {
    await dispatch(updateTaskAsync({ consultationId, taskId: task.id, patch: { done: !task.done } })).unwrap().catch(() => null);
    await onRefresh();
  };

  const remove = async () => {
    await dispatch(deleteTaskAsync({ consultationId, taskId: task.id })).unwrap().catch(() => null);
    await onRefresh();
  };

  return (
    <div className="insights-task">
      <input
        type="checkbox"
        checked={task.done}
        onChange={toggleDone}
        aria-label={task.done ? 'Mark task not done' : 'Mark task done'}
      />
      <input
        type="text"
        className={`insights-task__text${task.done ? ' is-done' : ''}`}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onBlur={persistDescription}
      />
      {task.isAi && <AiBadge />}
      <button type="button" className="insights-task__delete" onClick={remove}>
        Delete
      </button>
    </div>
  );
};

const MistakesList: React.FC<{
  consultationId: number;
  mistakes: ConsultationMistake[];
  onRefresh: () => void | Promise<void>;
  onPlay: (mistakeId: number, startMs: number, endMs: number | null) => void;
  onStop: () => void;
  playingMistakeId: number | null;
  canPlay: boolean;
}> = ({ consultationId, mistakes, onRefresh, onPlay, onStop, playingMistakeId, canPlay }) => {
  return (
    <div className="insights-mistakes">
      {mistakes.length === 0 && (
        <p className="insights-dialog__empty">No mistakes recorded yet.</p>
      )}
      {mistakes.map((m) => (
        <MistakeCard
          key={m.id}
          mistake={m}
          consultationId={consultationId}
          onRefresh={onRefresh}
          onPlay={onPlay}
          onStop={onStop}
          isPlaying={playingMistakeId === m.id}
          canPlay={canPlay}
        />
      ))}
    </div>
  );
};

const MistakeCard: React.FC<{
  mistake: ConsultationMistake;
  consultationId: number;
  onRefresh: () => void | Promise<void>;
  onPlay: (mistakeId: number, startMs: number, endMs: number | null) => void;
  onStop: () => void;
  isPlaying: boolean;
  canPlay: boolean;
}> = ({ mistake, consultationId, onRefresh, onPlay, onStop, isPlaying, canPlay }) => {
  const dispatch = useAppDispatch();
  const [editing, setEditing] = useState(false);
  const [category, setCategory] = useState<MistakeCategory>(mistake.category);
  const [quote, setQuote] = useState(mistake.quote || '');
  const [explanation, setExplanation] = useState(mistake.explanation);

  const [prevMistake, setPrevMistake] = useState(mistake);
  if (mistake !== prevMistake) {
    setPrevMistake(mistake);
    setCategory(mistake.category);
    setQuote(mistake.quote || '');
    setExplanation(mistake.explanation);
  }

  const save = async () => {
    if (!explanation.trim()) return;
    await dispatch(updateMistakeAsync({
      consultationId,
      mistakeId: mistake.id,
      patch: {
        category,
        quote: quote.trim() || null,
        explanation: explanation.trim()
      }
    })).unwrap().catch(() => null);
    setEditing(false);
    await onRefresh();
  };

  const remove = async () => {
    await dispatch(deleteMistakeAsync({ consultationId, mistakeId: mistake.id })).unwrap().catch(() => null);
    await onRefresh();
  };

  if (editing) {
    const selected = MISTAKE_CATEGORY_OPTIONS.find((o) => o.value === category) || null;
    return (
      <div className="insights-mistake">
        <div className="insights-mistake__edit">
          <div className="insights-mistake__edit-row">
            <div className="insights-mistake__category">
              <Select
                value={selected}
                onChange={(option) => { if (option) setCategory(option.value); }}
                options={MISTAKE_CATEGORY_OPTIONS}
                isClearable={false}
                placeholder="Category"
              />
            </div>
            <input
              type="text"
              value={quote}
              onChange={(e) => setQuote(e.target.value)}
              className="form-input"
              placeholder="Quote (optional)"
            />
          </div>
          <textarea
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            className="form-input"
            rows={3}
          />
          <div className="insights-mistake__edit-actions">
            <button type="button" className="btn-success" onClick={save} disabled={!explanation.trim()}>Save</button>
            <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  const labelForCategory = MISTAKE_CATEGORIES.find(c => c.value === mistake.category)?.label || mistake.category;
  const canPlayBack = canPlay && mistake.quoteStartMs != null;

  return (
    <div className="insights-mistake">
      <div className="insights-mistake__head">
        <span className="insights-mistake__tag">{labelForCategory}</span>
        {mistake.isAi && <AiBadge />}
        <div className="insights-mistake__actions">
          <button type="button" onClick={() => setEditing(true)} className="insights-mistake__action">Edit</button>
          <button type="button" onClick={remove} className="insights-mistake__action">Delete</button>
        </div>
      </div>
      {mistake.quote && (
        <div className="insights-mistake__quote">
          <p className="insights-mistake__quote-text">&ldquo;{mistake.quote}&rdquo;</p>
          {canPlayBack && (
            <button
              type="button"
              className={`insights-mistake__play${isPlaying ? ' is-playing' : ''}`}
              onClick={() => isPlaying ? onStop() : onPlay(mistake.id, mistake.quoteStartMs as number, mistake.quoteEndMs)}
              title={isPlaying ? 'Stop playback' : 'Play this moment'}
            >
              {isPlaying ? '■ Stop' : `▶ ${formatMs(mistake.quoteStartMs)}`}
            </button>
          )}
        </div>
      )}
      <p className="insights-mistake__note">{mistake.explanation}</p>
    </div>
  );
};

export default ConsultationAIInsightsSection;
